import { mapToAddressType, type Address } from "@/shared/@types/Address";
import type { FBSContainerListResponse } from "@/shared/generated/ExpHubApi";
import { mapToContractType, ContractType } from "./ContractType";
import { mapToGasTariffType, type GasTariff } from "./GasTariff";
import {
  mapToUsageForecastItemType,
  type UsageForecastItem,
} from "./UsageForecastItem";
import { mapToPaymentMethod, PaymentMethod } from "./PaymentMethod";

export interface GasContainer {
  serialNumber: string;
  name: string;
  address: Address;
  assetType?: string;
  serviceFlatRate?: number;
  equipmentNumber?: string;
  product: {
    name?: string;
    code?: string;
  };
  gasPrice: {
    includingVat?: number;
    excludingVat?: number;
    taxRate?: number;
    last: {
      excludingVat?: number;
    };
  };
  // Contract data for physical gas container
  orderContract: {
    id: string;
    type?: ContractType;
    startDate?: Date;
    endDate?: Date;
    version?: string;
  };
  // Contract id for corresponding meter (only if available, as not all containers have a meter contract)
  meterContractId?: string;
  volume: {
    max?: number;
    unit?: string;
  };
  fillLevel: {
    currentPercentage?: number;
    forecastedEmptyTankDate?: Date;
  };
  daysUntilDelivery?: number;
  hasOpenOrder: boolean;
  tariffs: Array<GasTariff>;
  usageForecast: Array<UsageForecastItem>;
  paymentMethod: PaymentMethod;
  supplyMode?: string; // Only relevant for tariff change

  // The following are used to determine if an order should be blocked
  deliveryBlock?: string;
  invoiceBlock?: string;
  deliveryBlockCustomer?: string;
  deliveryBlockCustomerBySalesOrganization?: string;
}

export const mapToGasContainerType = (
  container: FBSContainerListResponse["containers"][number],
  index: number,
  meterContractId?: string,
): GasContainer => {
  if (container.contract?.type !== ContractType.OrderContract) {
    throw new Error(`Expected ZVV contract, got: ${container.contract?.type}`);
  }

  const baseData = container;
  // Use parameter 'meterContractId' as additional safety net to assign a meter contract id to gas containers
  // that do not have a corresponding contract id in the API response as most customers have at most one meter contract
  const meterContract = baseData.correspondingContractId
    ? baseData.correspondingContractId
    : meterContractId;

  return {
    serialNumber: container.serialNumber,
    name: `Mein Behälter ${index + 1}`, // TODO: Add name when its implemented in the backend -> data.name ? data.name : `Mein Behälter ${index+1}`,
    assetType: container.assetType,
    serviceFlatRate: container.serviceFlatRate,
    address: mapToAddressType(container.address),
    equipmentNumber: container.equipmentNumber,
    product: {
      name: container.product.name,
      code: container.product.code,
    },
    gasPrice: {
      includingVat: container.price.gross,
      excludingVat: container.price.net,
      taxRate: container.price.tax,
      last: {
        excludingVat: container.price.last?.net,
      },
    },
    orderContract: {
      id: container.contractId,
      type: mapToContractType(container.contract.type),
      startDate: container.contract.startDate
        ? new Date(container.contract.startDate)
        : undefined,
      endDate: container.contract.endDate
        ? new Date(container.contract.endDate)
        : undefined,
      version: container.contract.version,
    },
    meterContractId: meterContract,
    volume: {
      max: container.volume.size,
      unit: container.volume.unit,
    },
    fillLevel: {
      currentPercentage: container.currentFillLevelPercentage,
      forecastedEmptyTankDate: container.forecastedEmptyTankDate
        ? new Date(container.forecastedEmptyTankDate)
        : undefined,
    },
    daysUntilDelivery: container.daysUntilDelivery,
    hasOpenOrder: container.openOrder ?? false,
    tariffs:
      container.bioTariffs?.map((tariff) => mapToGasTariffType(tariff)) ?? [],
    usageForecast:
      container.usageForecast?.map((forecast) =>
        mapToUsageForecastItemType(forecast),
      ) ?? [],
    deliveryBlock: container.deliveryBlock,
    invoiceBlock: container.invoiceBlock,
    deliveryBlockCustomer: container.deliveryBlockCustomer,
    deliveryBlockCustomerBySalesOrganization:
      container.deliveryBlockCustomerbySalesOrganization,
    paymentMethod: mapToPaymentMethod(container.paymentTerms),
    supplyMode: container.supplyMode,
  };
};
